import {
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"

import { getChartColorTokens, useChartTheme } from "~/constants/colors"
import { cn } from "~/utils/helpers"
import { formatNumber } from "~/utils/formatting"

export type MatrixValueDescription = { value: ReactNode; note?: ReactNode }

/** A `colors.ts` chart series, so the heat follows the same entry the charts use. */
export type MatrixHeatColor = keyof ReturnType<typeof getChartColorTokens>

export type MatrixProps = {
  /** Accessible name of the whole matrix, e.g. "Demand by size". */
  "aria-label": string
  rowHeaders: string[]
  columnHeaders: string[]
  /** `values[row][column]`; `null` is a combination that doesn't exist and renders `emptyText`. */
  values: (number | null)[][]
  /** Formats a cell or total. Gets the grand total so shares can be shown. Defaults to whole numbers. */
  formatValue?: (value: number, total: number) => ReactNode
  /** Tints each tile in 5 steps of its value relative to the largest cell, and shows the scale in the footer. Without it, values sit on the card with no tiles. */
  heat?: boolean
  /** Chart token the heat is drawn in; follows the measure (stock: `closingStock`). */
  heatColor?: MatrixHeatColor
  /** Adds a total column, and a total row with the grand total up to 10 columns. */
  totals?: boolean
  totalLabel?: string
  /** Footer: what every cell measures, e.g. "Share of demand". */
  measure?: string
  /** The customer's own name for the rows (e.g. "Length"). Only used in the tooltip and `matrixAxisLabel`, never in the grid; generic names are ignored. */
  rowAxisLabel?: string
  /** The customer's own name for the columns. Same rules as `rowAxisLabel`. */
  columnAxisLabel?: string
  /** Footer: the unit every cell is in, stated once in brackets after the measure (e.g. "%"). */
  unit?: string
  /** Marks a cell outside the current selection: no tint, value in fg-muted. */
  isMuted?: (row: number, column: number) => boolean
  /** Tooltip first line while the axis names aren't real: what the cell is stored as, e.g. the variant's size "28′-Ankle". With real names the line is built from them ("Waist 28 × Length 32"). */
  cellLabel?: (row: number, column: number) => string | undefined
  /** Which axis holds the first value of a combination, for the tooltip's real names: a size reads waist (the columns) before length. */
  firstAxis?: "rows" | "columns"
  /** Tooltip value lines: `value` ("1.573 units", "12% of demand") and an optional muted `note` (the share in Units). Defaults to the formatted value and unit. */
  describeValue?: (value: number, total: number) => MatrixValueDescription
  density?: "base" | "compact"
  emptyText?: string
  className?: string
}

const HEAT_STEP_OPACITY = [0, 0.12, 0.24, 0.36, 0.48, 0.6]
const LEGEND_STEPS = [1, 2, 3, 4, 5]

// Paddings in px, from Figma Matrix / Cell.
const DENSITY = {
  base: {
    tile: "h-6 rounded-[4px]",
    header: "h-[22px]",
    headerPadY: 6,
    rowPadY: 4,
    gap: 4,
    padRight: 8,
    rowHeaderPadLeft: 10,
    rowHeaderPadRight: 8,
    // Below the minimum the matrix scrolls instead of letting numbers run into each other.
    minTileWidth: 28,
    // Room a value keeps on each side before the matrix scrolls.
    valueBreathing: 3,
    totalColumnWidth: 48,
    footer: "px-2.5 py-2",
  },
  compact: {
    tile: "h-[22px] rounded-[3px]",
    header: "h-5",
    headerPadY: 5,
    rowPadY: 3,
    gap: 2,
    padRight: 6,
    rowHeaderPadLeft: 8,
    rowHeaderPadRight: 4,
    minTileWidth: 20,
    valueBreathing: 1.5,
    totalColumnWidth: 34,
    footer: "px-2 py-1.5",
  },
} as const

// Extra space before the total column, on top of the tile gap on each side.
const TOTAL_GAP = 6
// The row header column hugs its longest label within these bounds; longer labels truncate.
const ROW_HEADER_MIN = 48
const ROW_HEADER_MAX = 120
// Past this many columns the total row is left out (column totals would crowd); row totals stay.
const MAX_COLUMNS_WITH_TOTAL_ROW = 10
// Column headers up to this width (one word: "Copenhagen") never truncate; they widen their column.
const SHORT_HEADER_MAX = 80
// Paragraph/base-num is tabular, so every digit is the same width; separators are narrower.
const DIGIT_WIDTH = 8.4

const GENERIC_AXIS_NAME = /^size dimension \d+$/i

const isRealAxisName = (name: string | undefined): name is string =>
  !!name?.trim() && !GENERIC_AXIS_NAME.test(name.trim())

/**
 * "Length × Waist" for a card's meta slot: the customer's own dimension names, rows first. Use it
 * only when the card title doesn't already name the axes ("Stock units", not "Stock by category
 * and warehouse"). Undefined while either name is missing or generic ("Size dimension 1"), which
 * today is everyone.
 */
export function matrixAxisLabel(rowAxisLabel?: string, columnAxisLabel?: string) {
  return isRealAxisName(rowAxisLabel) && isRealAxisName(columnAxisLabel)
    ? `${rowAxisLabel} × ${columnAxisLabel}`
    : undefined
}

// Separators (thousands dots, decimal commas) are about half a digit wide.
const SEPARATOR_WIDTH = 3.5

/** Estimated width of a formatted value in Paragraph/base-num. */
const valueWidth = (node: ReactNode, fallback: number) => {
  const text =
    typeof node === "string" || typeof node === "number" ? String(node) : String(fallback)
  const separators = text.replace(/[^.,]/g, "").length
  return (text.length - separators) * DIGIT_WIDTH + separators * SEPARATOR_WIDTH
}

let labelContext: CanvasRenderingContext2D | null | undefined

/** Width of a Label/sm string as the browser draws it, so the row header column can hug it. */
function labelWidth(label: string) {
  if (labelContext === undefined) {
    labelContext =
      typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d")
  }
  if (!labelContext) return label.length * 7
  labelContext.font = `500 12px ${getComputedStyle(document.body).fontFamily}`
  return labelContext.measureText(label).width
}

const heatStep = (value: number, max: number) =>
  max > 0 && value > 0 ? Math.min(5, Math.ceil((value / max) * 5)) : 0

const sum = (values: (number | null | undefined)[]) =>
  values.reduce<number>((acc, v) => acc + (v ?? 0), 0)

const defaultFormat = (value: number) => formatNumber(value, 0)

const withUnit = (value: ReactNode, unit?: string): ReactNode => {
  if (!unit) return value
  const plain = typeof value === "string" || typeof value === "number"
  if (unit === "%") return plain ? `${value}%` : <>{value}%</>
  return plain ? (
    `${value} ${unit}`
  ) : (
    <>
      {value} {unit}
    </>
  )
}

// Text for a tile's accessible name; a formatted node that isn't plain text falls back to the raw number.
const plainText = (node: ReactNode, fallback: string) =>
  typeof node === "string" || typeof node === "number" ? String(node) : fallback

/** True while the scroller has content past its right edge, so the edge can fade out. */
function useOverflowsRight() {
  const ref = useRef<HTMLDivElement>(null)
  const [overflows, setOverflows] = useState(false)
  const update = () => {
    const el = ref.current
    if (el) setOverflows(el.scrollWidth - el.clientWidth - el.scrollLeft > 1)
  }
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new ResizeObserver(update)
    observer.observe(el)
    if (el.firstElementChild) observer.observe(el.firstElementChild)
    return () => observer.disconnect()
  }, [])
  return { ref, overflows, onScroll: update }
}

type ActiveCell = { row: number; column: number; rect: DOMRect }

/** The hovered or focused tile; cleared when the page scrolls, since the tooltip is fixed. */
function useActiveCell() {
  const [active, setActive] = useState<ActiveCell | null>(null)
  useEffect(() => {
    if (!active) return
    const clear = () => setActive(null)
    window.addEventListener("scroll", clear, true)
    return () => window.removeEventListener("scroll", clear, true)
  }, [active])
  return [active, setActive] as const
}

/**
 * Two-way table of numbers in a contained table (the Availability report pattern): one bordered
 * frame, a grey header row, hairline rows, values on tiles with an optional 5-step heat in the
 * measure's chart colour, a grey total row and the legend as the footer. Hover or focus a tile to
 * light up its headers and see the size and value in a tooltip. Fills its container and scrolls
 * sideways, row headers sticky, when the columns don't fit.
 * Canonical intent: {@link ../design-system/intents.ts MATRIX_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export function Matrix({
  "aria-label": ariaLabel,
  rowHeaders,
  columnHeaders,
  values,
  formatValue = defaultFormat,
  heat = false,
  heatColor = "salesDemand",
  totals = false,
  totalLabel = "Total",
  measure,
  rowAxisLabel,
  columnAxisLabel,
  unit,
  isMuted,
  cellLabel,
  firstAxis = "rows",
  describeValue,
  density = "base",
  emptyText = "–",
  className,
}: MatrixProps) {
  const theme = useChartTheme()
  const tooltipId = useId()
  const { ref: scrollerRef, overflows, onScroll } = useOverflowsRight()
  const [active, setActive] = useActiveCell()
  const heatToken = `var(--color-${getChartColorTokens(theme)[heatColor]})`
  const cellValues = values.flat().filter((v): v is number => v !== null)
  const total = sum(cellValues)
  const max = cellValues.reduce((m, v) => Math.max(m, v), 0)
  const caption = measure ? `${measure}${unit ? ` (${unit})` : ""}` : unit ? `(${unit})` : ""
  const showFooter = caption !== "" || heat

  const d = DENSITY[density]
  const showTotalRow = totals && columnHeaders.length <= MAX_COLUMNS_WITH_TOTAL_ROW
  const rowHeaderWidth = useMemo(() => {
    const labels = showTotalRow ? [...rowHeaders, totalLabel] : rowHeaders
    const widest = labels.reduce((w, label) => Math.max(w, labelWidth(label)), 0)
    const width = Math.ceil(widest) + d.rowHeaderPadLeft + d.rowHeaderPadRight
    return Math.min(ROW_HEADER_MAX, Math.max(ROW_HEADER_MIN, width))
  }, [rowHeaders, totalLabel, showTotalRow, d.rowHeaderPadLeft, d.rowHeaderPadRight])
  const columnTotals = columnHeaders.map((_, column) => sum(values.map((r) => r[column])))
  const rowTotals = values.map((r) => sum(r))
  const widest = (list: number[]) =>
    list.reduce((w, v) => Math.max(w, valueWidth(formatValue(v, total), v)), 0)
  // Short column headers (24′, Ankle) set a floor too; longer ones may truncate.
  const widestShortHeader = useMemo(
    () =>
      columnHeaders.reduce((w, header) => {
        const width = labelWidth(header)
        return width <= SHORT_HEADER_MAX ? Math.max(w, width) : w
      }, 0),
    [columnHeaders],
  )
  // Tiles never shrink below their widest value (column totals share the column).
  const minTileWidth = Math.max(
    d.minTileWidth,
    Math.ceil(widestShortHeader),
    Math.ceil(widest(cellValues)) + 2 * d.valueBreathing,
    showTotalRow ? Math.ceil(widest(columnTotals)) + d.valueBreathing : 0,
  )
  // Grows past its minimum only when a total would otherwise be clipped.
  const totalColumnWidth = Math.max(
    d.totalColumnWidth,
    Math.ceil(widest(showTotalRow ? [...rowTotals, total] : rowTotals)) + d.valueBreathing,
  )
  const totalCellPadLeft = 2 * d.gap + TOTAL_GAP
  const tableMinWidth =
    rowHeaderWidth +
    columnHeaders.length * (minTileWidth + d.gap) +
    (totals ? totalCellPadLeft + totalColumnWidth : 0) +
    d.padRight

  const activeTile = (row: number, column: number) =>
    active?.row === row && active.column === column
  // Same check as matrixAxisLabel: real names spell the combination out, first value first;
  // otherwise the name the cell is stored under, and bare headers as the last resort.
  const cellTitle = (row: number, column: number) => {
    const rowPart = `${rowAxisLabel} ${rowHeaders[row]}`
    const columnPart = `${columnAxisLabel} ${columnHeaders[column]}`
    if (matrixAxisLabel(rowAxisLabel, columnAxisLabel))
      return firstAxis === "columns" ? `${columnPart} × ${rowPart}` : `${rowPart} × ${columnPart}`
    return cellLabel?.(row, column) ?? `${rowHeaders[row]} × ${columnHeaders[column]}`
  }
  const describe = (value: number): MatrixValueDescription =>
    describeValue?.(value, total) ?? { value: withUnit(formatValue(value, total), unit) }
  // The tooltip's lines as one sentence, so a screen reader hears more than "12".
  const tileLabel = (row: number, column: number, value: number | null) => {
    const title = cellTitle(row, column)
    if (value === null) return `${title}, not made in this size`
    const { value: shown, note } = describe(value)
    const parts = [title, plainText(shown, String(value))]
    if (isMuted?.(row, column)) parts.push("outside the selection")
    else if (note) parts.push(plainText(note, ""))
    return parts.filter(Boolean).join(", ")
  }
  const tileEvents = (row: number, column: number, value: number | null) => {
    const open = (el: HTMLElement) => setActive({ row, column, rect: el.getBoundingClientRect() })
    return {
      tabIndex: 0,
      "aria-label": tileLabel(row, column, value),
      onMouseEnter: (e: MouseEvent<HTMLElement>) => open(e.currentTarget),
      onFocus: (e: FocusEvent<HTMLElement>) => open(e.currentTarget),
      onMouseLeave: () => setActive(null),
      onBlur: () => setActive(null),
      // Escape hides the tooltip; focus stays on the tile.
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        if (e.key === "Escape" && activeTile(row, column)) {
          e.stopPropagation()
          setActive(null)
        }
      },
    }
  }
  const headerHighlight = "rounded-[4px] bg-component text-fg-base"

  const lastValueColumn = columnHeaders.length - 1
  const valueCellStyle = (column: number, padY: number) => ({
    paddingTop: padY,
    paddingBottom: padY,
    paddingLeft: d.gap,
    paddingRight: !totals && column === lastValueColumn ? d.padRight : 0,
  })
  const totalCellStyle = (padY: number) => ({
    paddingTop: padY,
    paddingBottom: padY,
    paddingLeft: totalCellPadLeft,
    paddingRight: d.padRight,
  })
  const rowHeaderStyle = (padY: number) => ({
    paddingTop: padY,
    paddingBottom: padY,
    paddingLeft: d.rowHeaderPadLeft,
    paddingRight: d.rowHeaderPadRight,
  })
  const rowBorder = (row: number) =>
    row < rowHeaders.length - 1 || showTotalRow ? "border-b border-border-base" : ""
  const totalText = (value: number, grand = false) => (
    <div
      className={cn(
        "flex items-center justify-center whitespace-nowrap",
        d.tile,
        grand ? "label-base text-fg-base" : "paragraph-base text-fg-subtle",
      )}
    >
      {formatValue(value, total)}
    </div>
  )

  const activeValue = active ? (values[active.row]?.[active.column] ?? null) : null
  const activeMuted = active !== null && isMuted?.(active.row, active.column) === true
  const tooltipTitle = active && cellTitle(active.row, active.column)
  const description = activeValue === null ? null : describe(activeValue)

  return (
    <div
      className={cn(
        "flex w-full min-w-0 flex-col overflow-hidden rounded-[8px] border border-border-base bg-base",
        className,
      )}
    >
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className={cn(
          "overflow-x-auto",
          overflows && "[mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)]",
        )}
      >
        {/* TODO(HAK-3084, PR #5861): once the -num type styles merge, switch values and the grand
            total to label-base-num and totals to paragraph-base-num, and drop tabular-nums here. */}
        <table
          aria-label={ariaLabel}
          className="w-full table-fixed border-separate border-spacing-0 tabular-nums"
          style={{ minWidth: tableMinWidth }}
        >
          <colgroup>
            <col style={{ width: rowHeaderWidth }} />
            {columnHeaders.map((_, column) => (
              <col key={column} />
            ))}
            {totals && <col style={{ width: totalCellPadLeft + totalColumnWidth + d.padRight }} />}
          </colgroup>
          <thead>
            <tr>
              <td
                className="sticky left-0 z-10 border-b border-border-base bg-subtle p-0"
                style={rowHeaderStyle(d.headerPadY)}
              />
              {/* Index keys: headers are positional and may repeat (two warehouses with one name). */}
              {columnHeaders.map((header, column) => (
                <th
                  key={column}
                  scope="col"
                  className="border-b border-border-base bg-subtle p-0 font-normal"
                  style={valueCellStyle(column, d.headerPadY)}
                >
                  <div
                    className={cn(
                      "flex items-center justify-center label-sm text-fg-subtle transition-colors",
                      d.header,
                      active?.column === column && headerHighlight,
                    )}
                    title={header}
                  >
                    <span className="min-w-0 truncate">{header}</span>
                  </div>
                </th>
              ))}
              {totals && (
                <th
                  scope="col"
                  className="border-b border-border-base bg-subtle p-0 font-normal"
                  style={totalCellStyle(d.headerPadY)}
                >
                  <div
                    className={cn(
                      "flex items-center justify-center label-sm text-fg-subtle",
                      d.header,
                    )}
                  >
                    <span className="min-w-0 truncate">{totalLabel}</span>
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rowHeaders.map((rowHeader, row) => (
              <tr key={row}>
                <th
                  scope="row"
                  className={cn(
                    "sticky left-0 z-10 bg-base p-0 text-left font-normal",
                    rowBorder(row),
                  )}
                  style={rowHeaderStyle(d.rowPadY)}
                >
                  <div
                    className={cn(
                      "-mx-1 flex items-center px-1 label-sm text-fg-base transition-colors",
                      d.tile,
                      active?.row === row && headerHighlight,
                    )}
                    title={rowHeader}
                  >
                    <span className="truncate">{rowHeader}</span>
                  </div>
                </th>
                {columnHeaders.map((_, column) => {
                  const value = values[row]?.[column] ?? null
                  const cellStyle = valueCellStyle(column, d.rowPadY)
                  if (value === null)
                    return (
                      <td key={column} className={cn("p-0", rowBorder(row))} style={cellStyle}>
                        <div
                          {...tileEvents(row, column, value)}
                          className={cn(
                            "relative flex items-center justify-center paragraph-base text-fg-muted outline-none transition-shadow",
                            d.tile,
                            activeTile(row, column) && "z-10 shadow-borders-cell-hover",
                          )}
                        >
                          {emptyText}
                        </div>
                      </td>
                    )
                  const muted = isMuted?.(row, column) === true
                  const step = heat && !muted ? heatStep(value, max) : 0
                  return (
                    <td key={column} className={cn("p-0", rowBorder(row))} style={cellStyle}>
                      <div
                        {...tileEvents(row, column, value)}
                        className={cn(
                          "relative flex items-center justify-center overflow-hidden whitespace-nowrap paragraph-base outline-none transition-shadow",
                          d.tile,
                          heat && "bg-component",
                          muted ? "text-fg-muted" : "text-fg-base",
                          // The Availability report's heatmap hover ring; z-10 draws it over neighbouring tiles.
                          activeTile(row, column) && "z-10 shadow-borders-cell-hover",
                        )}
                      >
                        {step > 0 && (
                          <span
                            aria-hidden
                            className="absolute inset-0"
                            style={{ background: heatToken, opacity: HEAT_STEP_OPACITY[step] }}
                          />
                        )}
                        <span className="relative">{formatValue(value, total)}</span>
                      </div>
                    </td>
                  )
                })}
                {totals && (
                  <td className={cn("p-0", rowBorder(row))} style={totalCellStyle(d.rowPadY)}>
                    {totalText(rowTotals[row] ?? 0)}
                  </td>
                )}
              </tr>
            ))}
            {showTotalRow && (
              <tr>
                <th
                  scope="row"
                  className="sticky left-0 z-10 bg-subtle p-0 text-left font-normal"
                  style={rowHeaderStyle(d.rowPadY)}
                >
                  <div className={cn("flex items-center label-sm text-fg-subtle", d.tile)}>
                    <span className="truncate">{totalLabel}</span>
                  </div>
                </th>
                {columnTotals.map((columnTotal, column) => (
                  <td
                    key={column}
                    className="bg-subtle p-0"
                    style={valueCellStyle(column, d.rowPadY)}
                  >
                    {totalText(columnTotal)}
                  </td>
                ))}
                <td className="bg-subtle p-0" style={totalCellStyle(d.rowPadY)}>
                  {totalText(total, true)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showFooter && (
        <div
          className={cn(
            "flex items-center justify-between gap-3 border-t border-border-base",
            d.footer,
          )}
        >
          <span className="min-w-0 truncate paragraph-sm text-fg-subtle">{caption}</span>
          {heat && (
            <span
              className="flex shrink-0 items-center gap-1 text-xs leading-3 font-medium text-fg-muted tabular-nums"
              aria-hidden
            >
              {formatValue(0, total)}
              <span className="flex gap-0.5">
                {LEGEND_STEPS.map((step) => (
                  <span
                    key={step}
                    className="relative h-2 w-3 overflow-hidden rounded-[2px] bg-component"
                  >
                    <span
                      className="absolute inset-0"
                      style={{ background: heatToken, opacity: HEAT_STEP_OPACITY[step] }}
                    />
                  </span>
                ))}
              </span>
              {formatValue(max, total)}
            </span>
          )}
        </div>
      )}
      {/* Figma Matrix / Tooltip: one fact per line. One shared, fixed-position
          instance for every tile, portalled so the table's rounded frame doesn't clip it. */}
      {active &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className="pointer-events-none fixed z-[100000] max-w-[240px] -translate-x-1/2 rounded-[6px] border border-border-base bg-base px-2.5 py-2 text-fg-base shadow-tooltip"
            style={{ left: active.rect.left + active.rect.width / 2, top: active.rect.bottom + 6 }}
          >
            <div className="label-sm">{tooltipTitle}</div>
            {description ? (
              <>
                <div className="paragraph-sm tabular-nums">{description.value}</div>
                {activeMuted ? (
                  <div className="paragraph-sm text-fg-subtle">Outside the selection</div>
                ) : (
                  description.note && (
                    <div className="paragraph-sm text-fg-subtle tabular-nums">
                      {description.note}
                    </div>
                  )
                )}
              </>
            ) : (
              <div className="paragraph-sm text-fg-muted">Not made in this size</div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}
